import React, { useState, useCallback, useRef, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports -- TODO: legacy styled-components (baseline)
import styled from 'styled-components/macro';
import Hamburger from '../../../components/hamburger/hamburger';
import { injectRoleManagement } from '../../../utils/permissions/role-utils';
import { NavLink, matchPath } from 'react-router-dom';
import DeviceSelector from '../../../components/device-selector/device-selector';
// eslint-disable-next-line no-restricted-imports -- TODO: legacy connect (baseline)
import { connect } from 'react-redux';
import { selector as linksSelector } from '../../../redux/modules/secondary-nav';
import { useShallowStable } from '../private/components/app-launcher/hooks/use-shallow-stable';
import { desktopMediaCondition, mobileMediaCondition } from '../../../utils/helpers/device-utils';
import { userHavePlayerIdAndFeatureMask } from 'selectors/user/user-features-selector';
import { useOutsideClick } from 'utils/hooks/use-outside-click';

const Container = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-gap: 10px;
    align-items: center;
    max-width: 230px;
`;

const Item = ({ disabled, className, itemIsActive, ...props }) =>
    disabled ? (
        <div className={`${className} nl-disabled`}>{props.children}</div>
    ) : (
        // eslint-disable-next-line tpe/no-props-spread -- TODO: legacy props spread (baseline)
        <NavLink className={className} activeClassName="nl-active" active={itemIsActive.toString()} {...props} />
    );

const StyledItem = styled(Item)`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 4px 15px;

    color: ${({ itemIsActive }) => (itemIsActive ? 'var(--uiPrimaryColor)' : 'white')};

    font-weight: bold;
    text-decoration: none;

    cursor: pointer;

    &:hover,
    &:focus {
        color: ${({ itemIsActive }) => (itemIsActive ? 'var(--uiPrimaryColor)' : 'white')};
        text-decoration: none;

        background-color: var(--uiPrimaryColorTransparent);
        outline: none;
    }

    &.nl-disabled {
        color: var(--optionsDisabledColor);

        cursor: default;
    }

    &.nl-disabled:hover {
        background-color: unset;
    }
`;

const Popup = styled.div`
    position: absolute;
    top: 48px;

    display: flex;
    flex-direction: column;

    width: 110px;
    height: ${({ opened }) => (opened ? '100vh' : '0')};
    overflow: hidden;

    background-color: var(--uiHeaderColor);
    box-shadow: 3px 0 4px rgba(0, 0, 0, 0.25);

    transition: height 250ms;
    @media ${mobileMediaCondition} {
        left: ${props => ((props.level || 1) - 1) * 23}vw;

        width: 23.3vw;

        font-size: 0.7em;
    }
    @media ${desktopMediaCondition} {
        left: ${props => ((props.level || 1) - 1) * 150}px;

        width: 150px;
    }
`;

const ProductSelector = ({
    children,
    products,
    location,
    links,
    isUserPlayerOrGoalie,
    prospectExchangeAccount,
    ...roleProps
}) => {
    const [menuOpened, setMenuStatus] = useState(false);

    const containerRef = useRef();

    // The rest object is new on every render; a stable copy lets the memo below follow real role changes
    const stableRoleProps = useShallowStable(roleProps);
    const { pathname } = window.location;

    const visibleProducts = useMemo(() => {
        const visibleProducts = products.filter(link => link.isVisible(stableRoleProps));
        if (isUserPlayerOrGoalie) {
            return visibleProducts.filter(link => link.product !== 'SCOUT');
        }

        return visibleProducts;
    }, [products, isUserPlayerOrGoalie, stableRoleProps]);

    const currentLink = useMemo(
        () => links.filter(link => matchPath(pathname, { path: link.to })).sort((a, b) => b.to.length - a.to.length)[0],
        [links, pathname]
    );

    const currentSubmenu = useMemo(
        () =>
            currentLink?.submenu
                ?.filter(link => matchPath(pathname, { path: link.to }))
                .sort((a, b) => b.to.length - a.to.length)[0],
        [currentLink, pathname]
    );

    const noVisibleProducts = useMemo(() => visibleProducts.length === 0, [visibleProducts]);

    const closeMenu = useCallback(() => setMenuStatus(false), [setMenuStatus]);
    const toggleMenu = useCallback(() => setMenuStatus(s => !s), [setMenuStatus]);

    useOutsideClick({ ref: containerRef, callback: closeMenu, isEnabled: menuOpened });

    return (
        <Container ref={containerRef}>
            <Hamburger
                // eslint-disable-next-line tpe/no-inline-style -- TODO: legacy inline style (baseline)
                style={{
                    alignSelf: 'center',
                    padding: prospectExchangeAccount ? '5px' : 'none'
                }}
                color={prospectExchangeAccount ? 'var(--uiPrimaryColorDark)' : '#1f2937'}
                hamburgerIsActive={menuOpened}
                onClick={toggleMenu}
            />
            <DeviceSelector
                mobileLayout={
                    <>
                        <Popup opened={menuOpened}>
                            {links.map(link => (
                                <StyledItem
                                    itemIsActive={link === currentLink}
                                    key={link.to}
                                    to={
                                        (link.to.indexOf('analytics') >= 0 || link.to.indexOf('statistics') >= 0) &&
                                        link.submenu
                                            ? link.submenu?.[0]?.to
                                            : link.to
                                    }
                                    disabled={link.isEnabled && !link.isEnabled(roleProps)}
                                >
                                    {link.children}
                                </StyledItem>
                            ))}
                        </Popup>
                        {!!currentLink && !!currentLink.submenu && (
                            <Popup level={noVisibleProducts ? 2 : 3} opened={menuOpened}>
                                {currentLink.submenu.map(link => (
                                    <StyledItem
                                        itemIsActive={link === currentSubmenu}
                                        key={link.to}
                                        to={link.to}
                                        disabled={link.isEnabled && !link.isEnabled(roleProps)}
                                    >
                                        {link.children}
                                    </StyledItem>
                                ))}
                            </Popup>
                        )}
                    </>
                }
            />

            <div onClick={toggleMenu}>{children}</div>
        </Container>
    );
};

export default connect(state => ({
    links: linksSelector(state),
    isUserPlayerOrGoalie: userHavePlayerIdAndFeatureMask(state)
}))(injectRoleManagement(ProductSelector));
